<template>
  <article>
    <app-loading :loading="loading" is-background />

    <template v-if="!loading">
      <template v-if="anyShifts">
        <sme-alert v-if="lastSuccessfulSync === NA" level="danger">
          {{ $t('shiftStatus.noShiftDataAlert', { days: N_DAYS }) }}
        </sme-alert>
        <sme-alert v-else level="good">
          <i18n v-if="showShiftUploadDetail" path="shiftStatus.lastSyncDetail" tag="p">
            <template #new
              ><i>{{ $t('shiftStatus.newLabel') }}</i></template
            >
            <template #uploaded>{{ shiftsUploaded }}</template>
            <template #updated>{{ shiftsUpdated }}</template>
            <template #date>{{ toFormattedDateTime(lastSuccessfulSync) }}</template>
          </i18n>
          <i18n v-else path="shiftStatus.lastSyncSimple" tag="p">
            <template #new
              ><i>{{ $t('shiftStatus.newLabel') }}</i></template
            >
            <template #date>{{ toFormattedDateTime(lastSuccessfulSync) }}</template>
          </i18n>
        </sme-alert>
        <sme-alert
          v-if="employeesWithoutShiftsCount && !isUseUserCountInsteadOfEmployeesCount && !isNetEarningsEnabled"
          level="warning"
          class="mt-2"
        >
          <i18n
            :path="
              employeesWithoutShiftsCount > 1
                ? 'shiftStatus.noShiftsWorkedPlural'
                : 'shiftStatus.noShiftsWorkedSingular'
            "
            tag="span"
          >
            <template #employees>
              <sme-button-link @click="handleDisplayNoShifts" inherit>{{
                employeesWithoutShiftsCount > 1
                  ? $t('shiftStatus.employeeCountPlural', { count: employeesWithoutShiftsCount })
                  : $t('shiftStatus.employeeCountSingular')
              }}</sme-button-link>
            </template>
            <template #days>{{ N_DAYS }}</template>
          </i18n>
        </sme-alert>
        <employee-issue-list
          v-if="employeesWithoutShifts.length > 0 && showMissingShifts"
          :employees="employeesWithoutShifts"
          class="mt-2"
        />
        <sme-alert v-if="employeesWithoutShiftsCount === 0 && employeesWithShiftsCount >= 1" level="good" class="mt-2">
          {{ $t('shiftStatus.allEnrolledHaveShifts', { days: N_DAYS }) }}
        </sme-alert>
      </template>
      <sme-alert v-else level="danger">{{ $t('shiftStatus.noShiftsFound') }}</sme-alert>
    </template>
  </article>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import EmployeeIssueList from '@/components/EmployeeIssueList.vue';
import SmeAlert from '@/components/atoms/SmeAlert';
import SmeButtonLink from '@/components/atoms/SmeButtonLink';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { toFormattedDateTime } from '@/utils/date';

const { isUseUserCountInsteadOfEmployeesCount, isNetEarningsEnabled } = useFeatureFlags();

const N_DAYS = 8;
const NA = 'N/A';

const showMissingShifts = ref(false);
const loading = ref(true);
const anyShifts = ref(false);

const employeesWithoutShiftsCount = ref(0);
const employeesWithShiftsCount = ref(0);
const employeesWithoutShifts = ref([]);
const lastSuccessfulSync = ref(NA);
const shiftsUploaded = ref(undefined);
const shiftsUpdated = ref(undefined);

defineProps({
  showShiftUploadDetail: String,
});

const handleDisplayNoShifts = () => (showMissingShifts.value = !showMissingShifts.value);

const getEmployeeShiftData = async () => {
  const employeeData = (await ApiClient.smeGetEmployeesWithoutShifts(N_DAYS))?.data;
  employeesWithoutShiftsCount.value = employeeData.employees_without_shift;
  employeesWithoutShifts.value = employeeData.employees;
  employeesWithShiftsCount.value = employeeData.employees_with_shift;
};

const getLastSyncData = async () => {
  const shiftsData = await ApiClient.smeGetShiftsInLastNDays(N_DAYS);
  const lastSuccessfulSyncData = shiftsData?.data?.last_successful_sync;

  const createdOn = (Date.now() - Date.parse(State.state.company.created_at)) / (24 * 3600 * 1000);
  const allShiftsData = await ApiClient.smeGetShiftsInLastNDays(parseInt(Math.ceil(createdOn)));
  anyShifts.value = allShiftsData?.data?.syncs_in_time_period;

  if (lastSuccessfulSyncData && lastSuccessfulSyncData !== NA) {
    const completedAt = lastSuccessfulSyncData.created_at;
    const dateParts = completedAt.split('T');
    const roundedPart2 = dateParts[1].split('.')[0];
    lastSuccessfulSync.value = `${dateParts[0]} ${roundedPart2}`;
  }

  shiftsUploaded.value = shiftsData?.data?.shifts_created_count;
  shiftsUpdated.value = shiftsData?.data?.shifts_updated_count || 0;
};

onBeforeMount(async () => {
  await Promise.all([getEmployeeShiftData(), getLastSyncData()]);

  loading.value = false;
});
</script>
